import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { mediationsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  LifeBuoy,
  Shield,
  MessageSquare,
  CheckCircle2,
  ExternalLink,
  Calendar,
  Clock,
  RefreshCw,
  AlertTriangle,
  Trophy,
} from 'lucide-react';
import { io } from 'socket.io-client';
import ClubNameLabel from '../../components/ClubNameLabel';

const isItalianFn = (lang) => lang === 'it';

const formatRelative = (iso, isItalian) => {
  if (!iso) return '';
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return isItalian ? 'pochi secondi fa' : 'just now';
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return isItalian ? `${m} min fa` : `${m}m ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return isItalian ? `${h}h fa` : `${h}h ago`;
  }
  const d = Math.floor(diff / 86400);
  return isItalian ? `${d}g fa` : `${d}d ago`;
};

const statusBadge = (status, isItalian) => {
  switch (status) {
    case 'completed':
      return { label: isItalian ? 'Completata' : 'Completed', cls: 'bg-[#00E676]/20 text-[#00E676] border-[#00E676]/30' };
    case 'disputed':
      return { label: isItalian ? 'Contestata' : 'Disputed', cls: 'bg-[#FF1744]/20 text-[#FF1744] border-[#FF1744]/30' };
    case 'pending_report':
      return { label: '1 Report', cls: 'bg-[#FF9100]/20 text-[#FF9100] border-[#FF9100]/30' };
    default:
      return { label: isItalian ? 'Programmata' : 'Scheduled', cls: 'bg-[#FFEA00]/20 text-[#FFEA00] border-[#FFEA00]/30' };
  }
};

const Mediazioni = () => {
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const { i18n } = useTranslation();
  const isItalian = isItalianFn(i18n.language);
  const navigate = useNavigate();
  const [mediations, setMediations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);
  const [, setTick] = useState(0);

  const canAccess = isAdmin || isSuperAdmin;

  const fetchMediations = useCallback(async () => {
    try {
      const res = await mediationsApi.list();
      setMediations(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore nel caricamento' : 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [isItalian]);

  useEffect(() => {
    if (!canAccess) return;
    fetchMediations();
  }, [canAccess, fetchMediations]);

  // Live socket update
  useEffect(() => {
    if (!canAccess || !user) return;
    const BACKEND_URL = window.location.origin;
    const socket = io(BACKEND_URL, {
      path: '/api/sio/',
      transports: ['polling', 'websocket'],
      reconnection: true,
    });
    socket.on('connect', () => {
      socket.emit('register_user', {
        user_id: user.user_id,
        gamertag: user.gamertag,
        name: user.name,
        role: user.role,
      });
    });
    socket.on('mediation_pending_changed', () => {
      fetchMediations();
    });
    return () => { try { socket.disconnect(); } catch { /* ignore */ } };
  }, [canAccess, user, fetchMediations]);

  // Tick every 30s to refresh "X min ago"
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const handleResolve = async (m) => {
    const confirmMsg = isItalian
      ? `Marcare la mediazione tra ${m.home_club.name} e ${m.away_club.name} come risolta?`
      : `Mark the mediation between ${m.home_club.name} and ${m.away_club.name} as resolved?`;
    if (!window.confirm(confirmMsg)) return;
    setResolving(m.match_id);
    try {
      await mediationsApi.resolve(m.match_id);
      toast.success(isItalian ? 'Mediazione chiusa' : 'Mediation closed');
      setMediations((prev) => prev.filter((x) => x.match_id !== m.match_id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore' : 'Error'));
    } finally {
      setResolving(null);
    }
  };

  if (!canAccess) {
    return (
      <div className="min-h-screen ept-bg-ambient text-white p-6 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 mx-auto text-[#FF1744]/60 mb-3" />
          <p className="text-slate-400">{isItalian ? 'Accesso riservato agli admin' : 'Admin access only'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ept-bg-ambient text-white p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded bg-[#FF00E5]/15 border border-[#FF00E5]/40 flex items-center justify-center">
              <LifeBuoy className="w-6 h-6 text-[#FF00E5]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold uppercase tracking-wider">
                {isItalian ? 'Mediazioni Pending' : 'Pending Mediations'}
              </h1>
              <p className="text-xs text-slate-500 uppercase tracking-widest">
                {isItalian ? 'Richieste aperte di intervento admin' : 'Open admin help requests'}
              </p>
            </div>
          </div>
          <Button
            onClick={fetchMediations}
            className="bg-white/5 border border-white/10 hover:bg-white/10 text-slate-300"
            data-testid="mediations-refresh-btn"
          >
            <RefreshCw size={16} className="mr-2" />
            {isItalian ? 'Aggiorna' : 'Refresh'}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-500" data-testid="mediations-loading">
            {isItalian ? 'Caricamento...' : 'Loading...'}
          </div>
        ) : mediations.length === 0 ? (
          <div className="text-center py-16 bg-[#0a0d15] border border-white/10 rounded" data-testid="mediations-empty">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#00E676]/60 mb-3" />
            <p className="text-slate-300 font-medium">
              {isItalian ? 'Nessuna mediazione aperta' : 'No open mediations'}
            </p>
            <p className="text-xs text-slate-500 mt-1 uppercase tracking-widest">
              {isItalian ? 'Bel lavoro, admin!' : 'Great job, admin!'}
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="mediations-list">
            <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">
              {mediations.length} {isItalian ? 'aperte — ordinate dalla più vecchia' : 'open — oldest first'}
            </div>
            {mediations.map((m) => {
              const sb = statusBadge(m.status, isItalian);
              return (
                <div
                  key={m.match_id}
                  className="bg-[#0a0d15] border border-[#FF00E5]/30 hover:border-[#FF00E5]/60 transition-colors p-4 rounded"
                  data-testid={`mediation-card-${m.match_id}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                      {m.championship_name && (
                        <span className="flex items-center gap-1 text-[#00F0FF]">
                          <Trophy size={12} /> {m.championship_name}
                        </span>
                      )}
                      {m.match_date && (
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {new Date(m.match_date).toLocaleDateString('it-IT', {
                            day: '2-digit', month: 'short', year: 'numeric'
                          })}
                          {' • '}
                          {new Date(m.match_date).toLocaleTimeString('it-IT', {
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded uppercase tracking-widest border text-[10px] ${sb.cls}`}>
                        {sb.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-[#FFEA00]">
                      <Clock size={12} />
                      <span className="font-mono font-bold">
                        {isItalian ? 'Aperta' : 'Open'} {formatRelative(m.requested_at, isItalian)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1 text-center min-w-0">
                      <div
                        className="w-10 h-10 mx-auto mb-1 bg-[#050508] border border-white/10 flex items-center justify-center overflow-hidden rounded"
                      >
                        {m.home_club.logo ? (
                          <img src={m.home_club.logo} alt={m.home_club.name} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <Shield size={18} className="text-white/60" />
                        )}
                      </div>
                      <ClubNameLabel as="p" name={m.home_club.name} className="text-xs font-medium" />
                    </div>
                    <div className="px-3 text-center flex-shrink-0">
                      {(m.status === 'completed' || m.status === 'disputed' || (m.home_score !== null && m.home_score !== undefined)) ? (
                        <div className="font-mono text-lg font-bold">
                          <span className="text-white">{m.home_score ?? '-'}</span>
                          <span className="text-slate-600 mx-1">-</span>
                          <span className="text-white">{m.away_score ?? '-'}</span>
                        </div>
                      ) : (
                        <div className="font-mono text-lg font-bold text-slate-500">VS</div>
                      )}
                    </div>
                    <div className="flex-1 text-center min-w-0">
                      <div className="w-10 h-10 mx-auto mb-1 bg-[#050508] border border-white/10 flex items-center justify-center overflow-hidden rounded">
                        {m.away_club.logo ? (
                          <img src={m.away_club.logo} alt={m.away_club.name} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <Shield size={18} className="text-white/60" />
                        )}
                      </div>
                      <ClubNameLabel as="p" name={m.away_club.name} className="text-xs font-medium" />
                    </div>
                  </div>

                  {m.requester_label && (
                    <div className="flex items-center gap-2 text-xs text-slate-400 bg-white/[0.02] border border-white/5 rounded p-2 mb-3">
                      <AlertTriangle size={12} className="text-[#FF00E5] flex-shrink-0" />
                      <span>
                        {isItalian ? 'Richiesta da' : 'Requested by'}{' '}
                        <strong className="text-white">{m.requester_label}</strong>
                      </span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Button
                      onClick={() => navigate(`/match-chats?open=${m.match_id}`)}
                      className="bg-[#00F0FF]/10 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/20"
                      data-testid={`mediation-open-chat-${m.match_id}`}
                    >
                      <MessageSquare size={14} className="mr-2" />
                      {isItalian ? 'Apri Chat Partita' : 'Open Match Chat'}
                    </Button>
                    <Button
                      onClick={() => navigate(`/tornei-partite?focus=${m.match_id}`)}
                      className="bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10"
                      data-testid={`mediation-go-match-${m.match_id}`}
                    >
                      <ExternalLink size={14} className="mr-2" />
                      {isItalian ? 'Vai al Match' : 'Go to Match'}
                    </Button>
                    <Button
                      onClick={() => handleResolve(m)}
                      disabled={resolving === m.match_id}
                      className="bg-[#00E676]/15 border border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/25 disabled:opacity-50"
                      data-testid={`mediation-resolve-${m.match_id}`}
                    >
                      {resolving === m.match_id ? (
                        <span className="animate-spin w-4 h-4 border-2 border-[#00E676] border-t-transparent rounded-full mr-2" />
                      ) : (
                        <CheckCircle2 size={14} className="mr-2" />
                      )}
                      {isItalian ? 'Marca come Risolta' : 'Mark as Resolved'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Mediazioni;
